import { Box, Button, Skeleton, Theme, Typography, useTheme } from "@mui/material"
import { useEffect, useState } from "react"
import { ErrorText } from "../dashboard/chart/CustomeLineChart"
import CustomNavChart from "../dashboard/chart/CustomNavChart"
import { NO_DATA_ERROR_MESSAGE } from "../../utils/constant"

interface IndNavBtnProps {
    type: string,
    count?: number,
    text: string
}

const navChartButtons = [{
    type: 'month',
    count: 6,
    text: '6M'
}, {
    type: 'year',
    count: 1,
    text: '1Y'
},
{
    type: 'all',
    text: 'Inception'
}]

type PerformanceProps = {
    isLoading: boolean
    getAllData?: {
        data1: Array<[number, number]>,
        data2: Array<[number, number]>,
        label1: string;
        label2: string;
    }
    dynamicY: boolean;
    success?: boolean;
}
export const PortfolioHealthChart = (props: PerformanceProps): JSX.Element => {
    const { isLoading, getAllData, success } = props
    const { palette } = useTheme()
    const [navChartBtn, setNavChartBtn] = useState('Inception');
    const [filteredData, setFilteredData] = useState<Array<[number, number]>>(getAllData?.data1 as Array<[number, number]>);
    const [filteredData2, setFilteredData2] = useState<Array<[number, number]>>(getAllData?.data2 as Array<[number, number]>);
    const label1 = `${getAllData?.label1 ?? ""}`
    const label2 = `${getAllData?.label2 ?? ""}`;
    const currentDate = new Date();
    const cutoffDate = (n: number) => new Date(currentDate.getFullYear() - n, 0, 1);

    useEffect(() => {
        if (getAllData?.data1) setFilteredData([...getAllData?.data1])
        if (getAllData?.data2) setFilteredData2([...getAllData?.data2])
    }, [getAllData])

    const handleNavChartBtn = (btn: string) => {
        setNavChartBtn(btn);
        if (btn === '6M') {
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
            setFilteredData(getAllData?.data1.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || []);
            setFilteredData2(getAllData?.data2.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()) || []);
        } else if (btn === '1Y') {
            setFilteredData(getAllData?.data1.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || []);
            setFilteredData2(getAllData?.data2.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()) || []);
        } else if (btn === 'Inception') {
            setFilteredData(getAllData?.data1 || []);
            setFilteredData2(getAllData?.data2 || []);
        }
    };

    return (
        <Box sx={{
            padding: '20px 30px',
            borderRadius: '10px',
            // padding: '25px 30px 45px 30px',
            background: palette.common.white,
            // borderRadius: '20px',
            height: '100%',
            // border: `3px solid ${palette.stroke.main}`,
        }}>
            {isLoading && (
                <>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        rowGap: '10px',
                        mb: '55px'
                    }}>
                        <Typography
                            variant="h2"
                            color='text.primary'
                        >
                            Portfolio Performance
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: { sm: 'flex-end', md: 'flex-start' },
                            columnGap: 3,
                        }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    columnGap: '7px'
                                }}>
                                <Skeleton
                                    variant="circular"
                                    width={11}
                                    height={11}
                                    animation="wave"
                                />
                                <Skeleton
                                    animation="wave"
                                    sx={{ fontSize: '16px' }}
                                    width="5rem"
                                />
                            </Box>

                        </Box>
                    </Box>
                    <Box>
                        <Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Skeleton variant="rectangular" width={10} height={130} />
                                <Skeleton variant="rectangular" width={10} height={130} />
                                <Skeleton variant="rectangular" width={10} height={130} />
                            </Box>
                            <Box sx={{ display: 'flex' }}>
                                <Skeleton variant="rectangular" width='100%' height={10} />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                mt: '45px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                columnGap: '10px'
                            }}
                        >
                            {navChartButtons?.map((individualBtn: IndNavBtnProps, index: number) => {
                                return (
                                    <Button
                                        key={index}
                                        sx={(theme: Theme) => ({
                                            ...theme.typography.body_500,
                                            letterSpacing: '0.02px',
                                            borderRadius: '7px',
                                            padding: '5px 15px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: { color: navChartBtn === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary },
                                            '&:hover': {
                                                border: 'none',
                                            }
                                        }
                                        )}
                                        variant={navChartBtn === individualBtn?.text ? 'contained' : 'outlined'}
                                        onClick={() => handleNavChartBtn(individualBtn?.text)}
                                        disabled={true}
                                    >
                                        {individualBtn?.text}
                                    </Button>
                                )
                            })}
                        </Box>
                    </Box>
                </>
            )}
            {(!isLoading && !success) && (
                <Box>
                    <Typography variant="h2" color='text.primary'>Portfolio Performance</Typography>
                    <Box sx={{ mt: '35px' }}>
                        <Typography
                            variant="body_600"
                            sx={(theme: Theme) => ({
                                fontWeight: 500,
                                lineHeight: "22.5px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary,
                                textAlign: 'center',
                            })}
                        >
                            {NO_DATA_ERROR_MESSAGE}
                        </Typography>
                    </Box>

                </Box>
            )}
            {!isLoading && success &&
                <>
                    <Box sx={(theme: Theme) => ({
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        rowGap: '10px',
                        mb: '55px'
                    })}>
                        <Typography variant="h2" color='text.primary'>Portfolio Performance</Typography>
                        <Box sx={(theme: Theme) => ({
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: { sm: 'flex-end', md: 'flex-start' },
                            columnGap: { sm: 0, md: 2, lg: 3 },
                        })}>
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '7px' }}>
                                <Box component={'span'} sx={(theme: Theme) => (({ width: '11px', height: '11px', background: theme.palette.primary[500], borderRadius: '50%' }))}></Box>
                                <Typography component={'span'} variant="body_500" color="text.primary">{label1}</Typography>
                            </Box>
                            {/* <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '7px' }}>
                            <Box component={'span'} sx={(theme: Theme) => (({ width: '11px', height: '11px', background: theme.palette.primary[100], borderRadius: '50%' }))}></Box>
                            <Typography component={'span'} variant="body_500" color="text.primary">{label2}</Typography>
                        </Box> */}

                        </Box>
                    </Box>

                    <Box>
                        <Box sx={{ height: "400px" }}>
                            {(filteredData?.length === 0 && filteredData2?.length === 0) ?
                                <ErrorText /> :
                                <CustomNavChart
                                    data={filteredData}
                                    // data1={filteredData2}
                                    label1={label1}
                                    // label2={label2}
                                    dynamicY={true} />
                            }
                        </Box>
                        <Box sx={{ mt: '45px', display: 'flex', justifyContent: 'center', alignItems: 'center', columnGap: '10px' }} >
                            {navChartButtons?.map((individualBtn: IndNavBtnProps, index: number) => {
                                return (
                                    <Button sx={(theme: Theme) => (
                                        {
                                            ...theme.typography.body_500, letterSpacing: '0.02px', borderRadius: '7px', padding: '5px 15px', border: 'none', cursor: 'pointer', color: { color: navChartBtn === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary }, '&:hover': {
                                                border: 'none',
                                            }
                                        }
                                    )} variant={navChartBtn === individualBtn?.text ? 'contained' : 'outlined'} key={index}
                                        onClick={() => handleNavChartBtn(individualBtn?.text)}
                                    >{individualBtn?.text}</Button>
                                )
                            })}
                        </Box>
                    </Box>
                </>
            }


        </Box>
    )
}